import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const TestingDashboard = () => {
  const [testResults, setTestResults] = useState({
    unit: { passed: 45, failed: 2, total: 47, coverage: 87 },
    e2e: { passed: 28, failed: 1, total: 29, coverage: 96 },
    accessibility: { passed: 15, failed: 0, total: 15, coverage: 100 },
    performance: { passed: 8, failed: 0, total: 8, coverage: 100 }
  });

  const [metrics, setMetrics] = useState({
    buildTime: '2.3s',
    bundleSize: '1.2MB',
    lighthouse: { performance: 95, accessibility: 98, bestPractices: 92, seo: 89 },
    coreWebVitals: { lcp: 1.2, fid: 45, cls: 0.05 }
  });

  const [qualityScore, setQualityScore] = useState(94);

  const getStatusColor = (passed, total) => {
    const percentage = (passed / total) * 100;
    if (percentage >= 90) return 'text-green-400';
    if (percentage >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusIcon = (passed, total) => {
    const percentage = (passed / total) * 100;
    if (percentage >= 90) return '✅';
    if (percentage >= 70) return '⚠️';
    return '❌';
  };

  return (
    <div className="min-h-screen bg-black text-silver p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-px h-8 bg-silver/30"></div>
            <p className="text-xs font-mono tracking-[0.4em] text-silver/50 uppercase">
              Quality Assurance
            </p>
            <div className="w-px h-8 bg-silver/30"></div>
          </div>
          <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-tight">
            Testing Dashboard
          </h1>
          <p className="text-xl text-silver/70 mb-8 font-light">
            Comprehensive test suite ensuring codebase integrity and quality
          </p>
        </motion.div>

        {/* Quality Score */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm mb-8"
        >
          <div className="text-center">
            <div className="text-6xl font-light mb-4">{qualityScore}%</div>
            <div className="text-silver/70 font-light">Overall Quality Score</div>
          </div>
        </motion.div>

        {/* Test Results Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Object.entries(testResults).map(([category, data], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-black/50 p-6 rounded-2xl border border-silver/10 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium capitalize">{category} Tests</h3>
                <span className="text-2xl">{getStatusIcon(data.passed, data.total)}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-silver/70">Passed:</span>
                  <span className="text-green-400">{data.passed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-silver/70">Failed:</span>
                  <span className="text-red-400">{data.failed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-silver/70">Total:</span>
                  <span className="text-silver">{data.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-silver/70">Coverage:</span>
                  <span className={getStatusColor(data.coverage, 100)}>{data.coverage}%</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Performance Metrics */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-light mb-6">Performance Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-silver/70">Build Time:</span>
                <span className="text-silver">{metrics.buildTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-silver/70">Bundle Size:</span>
                <span className="text-silver">{metrics.bundleSize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-silver/70">LCP:</span>
                <span className="text-green-400">{metrics.coreWebVitals.lcp}s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-silver/70">FID:</span>
                <span className="text-green-400">{metrics.coreWebVitals.fid}ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-silver/70">CLS:</span>
                <span className="text-green-400">{metrics.coreWebVitals.cls}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-light mb-6">Lighthouse Scores</h3>
            <div className="space-y-4">
              {Object.entries(metrics.lighthouse).map(([metric, score]) => (
                <div key={metric} className="flex justify-between items-center">
                  <span className="text-silver/70 capitalize">{metric}:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          score >= 90 ? 'bg-green-400' : score >= 70 ? 'bg-yellow-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-silver">{score}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Test Categories */}
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-light mb-6">Testing Tools</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Jest - Unit Testing</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Playwright - E2E Testing</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Storybook - Component Testing</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">ESLint - Code Quality</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Prettier - Code Formatting</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Husky - Git Hooks</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-black/50 p-8 rounded-2xl border border-silver/10 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-light mb-6">Quality Standards</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">80%+ Test Coverage</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Accessibility Compliance</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Performance Optimization</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Responsive Design</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">SEO Best Practices</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-400">✓</span>
                <span className="text-silver">Security Standards</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
        >
          <button className="bg-silver/10 text-silver border border-silver/30 py-4 px-8 rounded-xl font-light hover:bg-silver/20 hover:border-silver/50 transition-all duration-500">
            Run All Tests
          </button>
          <button className="bg-silver/10 text-silver border border-silver/30 py-4 px-8 rounded-xl font-light hover:bg-silver/20 hover:border-silver/50 transition-all duration-500">
            View Coverage Report
          </button>
          <button className="bg-silver/10 text-silver border border-silver/30 py-4 px-8 rounded-xl font-light hover:bg-silver/20 hover:border-silver/50 transition-all duration-500">
            Open Storybook
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default TestingDashboard; 